import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
    Type, Heading1, AlignCenter, FileText, Save, Eye, 
    Bookmark, SplitSquareVertical, RotateCcw, Check, Palette
} from 'lucide-react';

// Storage key for formatting settings
const getFormattingKey = (bookId) => `eskrybe-formatting-${bookId}`;

// Default formatting presets
const HEADING_PRESETS = {
    classic: {
        name: 'Classic',
        description: 'Traditional chapter headings centered with elegant spacing',
        chapterPrefix: 'Chapter',
        showNumber: true,
        numberStyle: 'numeric', // numeric, roman, spelled
        alignment: 'center',
        titleCase: 'uppercase',
        fontSize: 'large',
        fontFamily: 'serif',
        spacing: {
            beforeChapter: '4rem',
            afterNumber: '0.5rem',
            afterTitle: '2rem'
        },
        decoration: 'none'
    },
    modern: {
        name: 'Modern',
        description: 'Clean, minimalist chapter headings with subtle styling',
        chapterPrefix: '',
        showNumber: true,
        numberStyle: 'numeric',
        alignment: 'left',
        titleCase: 'capitalize',
        fontSize: 'medium',
        fontFamily: 'sans',
        spacing: {
            beforeChapter: '3rem',
            afterNumber: '0.25rem',
            afterTitle: '1.5rem'
        },
        decoration: 'underline'
    },
    literary: {
        name: 'Literary',
        description: 'Elegant roman numerals with ornamental flourishes',
        chapterPrefix: '',
        showNumber: true,
        numberStyle: 'roman',
        alignment: 'center',
        titleCase: 'uppercase',
        fontSize: 'medium',
        fontFamily: 'serif',
        spacing: {
            beforeChapter: '5rem',
            afterNumber: '1rem',
            afterTitle: '2.5rem'
        },
        decoration: 'ornament'
    },
    thriller: {
        name: 'Thriller',
        description: 'Bold, impactful chapter openings for tension',
        chapterPrefix: '',
        showNumber: true,
        numberStyle: 'numeric',
        alignment: 'left',
        titleCase: 'uppercase',
        fontSize: 'xlarge',
        fontFamily: 'sans',
        spacing: {
            beforeChapter: '2rem',
            afterNumber: '0',
            afterTitle: '1rem'
        },
        decoration: 'bold-line'
    },
    romance: {
        name: 'Romance',
        description: 'Soft, flowing chapter headers with decorative elements',
        chapterPrefix: 'Chapter',
        showNumber: true,
        numberStyle: 'spelled',
        alignment: 'center',
        titleCase: 'capitalize',
        fontSize: 'medium',
        fontFamily: 'script',
        spacing: {
            beforeChapter: '4rem',
            afterNumber: '0.75rem',
            afterTitle: '2rem'
        },
        decoration: 'flourish'
    },
    minimal: {
        name: 'Minimal',
        description: 'Simple numbers only, maximum focus on content',
        chapterPrefix: '',
        showNumber: true,
        numberStyle: 'numeric',
        alignment: 'center',
        titleCase: 'none',
        fontSize: 'small',
        fontFamily: 'sans',
        spacing: {
            beforeChapter: '3rem',
            afterNumber: '0',
            afterTitle: '2rem'
        },
        decoration: 'none'
    }
};

// Page break options
const PAGE_BREAK_OPTIONS = {
    chapters: {
        label: 'Before each chapter',
        description: 'Start each chapter on a new page'
    },
    parts: {
        label: 'Before parts only',
        description: 'New page for major sections'
    },
    none: {
        label: 'No page breaks',
        description: 'Continuous flow between chapters'
    }
};

// Section formatting options
const SECTION_BREAK_STYLES = [
    { id: 'blank', label: 'Blank Line', preview: '⠀' },
    { id: 'asterisks', label: 'Three Asterisks', preview: '* * *' },
    { id: 'ornament', label: 'Ornament', preview: '❧' },
    { id: 'dash', label: 'Em Dash', preview: '—' },
    { id: 'dots', label: 'Three Dots', preview: '• • •' },
    { id: 'custom', label: 'Custom', preview: '~' }
];

const ProBookFormatting = ({ 
    bookId, 
    chapters,
    onApplyFormatting 
}) => {
    const [activeTab, setActiveTab] = useState('headings');
    const [selectedPreset, setSelectedPreset] = useState('classic');
    const [customSettings, setCustomSettings] = useState(HEADING_PRESETS.classic);
    const [pageBreaks, setPageBreaks] = useState('chapters');
    const [sectionBreakStyle, setSectionBreakStyle] = useState('asterisks');
    const [customSectionBreak, setCustomSectionBreak] = useState('~');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Load saved settings
    useEffect(() => {
        const saved = localStorage.getItem(getFormattingKey(bookId));
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setSelectedPreset(parsed.selectedPreset || 'classic');
                setCustomSettings(parsed.customSettings || HEADING_PRESETS.classic);
                setPageBreaks(parsed.pageBreaks || 'chapters');
                setSectionBreakStyle(parsed.sectionBreakStyle || 'asterisks');
                setCustomSectionBreak(parsed.customSectionBreak || '~');
            } catch (e) {
                console.error('Failed to load formatting settings', e);
            }
        }
    }, [bookId]);

    // Save settings
    const handleSave = () => {
        const settings = {
            selectedPreset,
            customSettings,
            pageBreaks,
            sectionBreakStyle,
            customSectionBreak
        };
        localStorage.setItem(getFormattingKey(bookId), JSON.stringify(settings));
        setHasUnsavedChanges(false);
        toast.success('Formatting settings saved');
    };

    // Apply preset
    const handlePresetChange = (presetId) => {
        setSelectedPreset(presetId);
        if (presetId !== 'custom') {
            setCustomSettings(HEADING_PRESETS[presetId]);
        }
        setHasUnsavedChanges(true);
    };

    // Update custom setting
    const updateCustomSetting = (key, value) => {
        setSelectedPreset('custom');
        setCustomSettings(prev => ({
            ...prev,
            [key]: value
        }));
        setHasUnsavedChanges(true);
    };

    // Convert number to roman numeral
    const toRoman = (num) => {
        const romanNumerals = [
            ['M', 1000], ['CM', 900], ['D', 500], ['CD', 400],
            ['C', 100], ['XC', 90], ['L', 50], ['XL', 40],
            ['X', 10], ['IX', 9], ['V', 5], ['IV', 4], ['I', 1]
        ];
        let result = '';
        for (const [letter, value] of romanNumerals) {
            while (num >= value) {
                result += letter;
                num -= value;
            }
        }
        return result;
    };

    // Spell out number
    const spellNumber = (num) => {
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        if (num < 20) return ones[num];
        if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? '-' + ones[num % 10] : '');
        return num.toString();
    };

    // Get chapter number display
    const getChapterNumber = (num) => {
        switch (customSettings.numberStyle) {
            case 'roman': return toRoman(num);
            case 'spelled': return spellNumber(num);
            default: return num.toString();
        }
    };

    // Render chapter preview
    const renderChapterPreview = (chapter, index) => {
        const num = index + 1;
        const numberDisplay = customSettings.showNumber ? getChapterNumber(num) : '';
        const prefix = customSettings.chapterPrefix;
        
        let titleText = chapter?.title || 'The Beginning';
        switch (customSettings.titleCase) {
            case 'uppercase': titleText = titleText.toUpperCase(); break;
            case 'lowercase': titleText = titleText.toLowerCase(); break;
            case 'capitalize': titleText = titleText.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '); break;
            default: break;
        }

        const fontFamilyClass = {
            serif: 'font-serif',
            sans: 'font-sans',
            script: 'font-serif italic'
        }[customSettings.fontFamily] || 'font-serif';

        const fontSizeClass = {
            small: 'text-lg',
            medium: 'text-xl',
            large: 'text-2xl',
            xlarge: 'text-3xl'
        }[customSettings.fontSize] || 'text-xl';

        const alignClass = {
            left: 'text-left',
            center: 'text-center',
            right: 'text-right'
        }[customSettings.alignment] || 'text-center';

        return (
            <div 
                className={`p-6 bg-white dark:bg-zinc-900 rounded-lg border shadow-sm ${alignClass}`}
                style={{ marginTop: customSettings.spacing.beforeChapter }}
            >
                {/* Chapter Number */}
                {prefix && numberDisplay && (
                    <div 
                        className={`text-muted-foreground text-sm tracking-widest ${fontFamilyClass}`}
                        style={{ marginBottom: customSettings.spacing.afterNumber }}
                    >
                        {prefix} {numberDisplay}
                    </div>
                )}
                {!prefix && numberDisplay && (
                    <div 
                        className={`text-muted-foreground ${fontSizeClass} ${fontFamilyClass}`}
                        style={{ marginBottom: customSettings.spacing.afterNumber }}
                    >
                        {numberDisplay}
                    </div>
                )}

                {/* Decoration */}
                {customSettings.decoration === 'ornament' && (
                    <div className="text-accent text-2xl my-2">❧</div>
                )}
                {customSettings.decoration === 'flourish' && (
                    <div className="text-accent my-2">~ ✦ ~</div>
                )}

                {/* Title */}
                <h1 
                    className={`${fontSizeClass} ${fontFamilyClass} font-bold tracking-wide`}
                    style={{ marginBottom: customSettings.spacing.afterTitle }}
                >
                    {titleText}
                </h1>

                {/* Underline decoration */}
                {customSettings.decoration === 'underline' && (
                    <div className="w-16 h-0.5 bg-accent mx-auto mt-2" style={customSettings.alignment === 'left' ? { marginLeft: 0 } : {}} />
                )}
                {customSettings.decoration === 'bold-line' && (
                    <div className="w-24 h-1 bg-foreground mt-2" style={customSettings.alignment === 'left' ? { marginLeft: 0 } : { margin: '0.5rem auto' }} />
                )}
            </div>
        );
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-serif text-2xl font-semibold flex items-center gap-2">
                        <Type className="h-6 w-6 text-accent" />
                        Pro Book Formatting
                    </h2>
                    <p className="text-muted-foreground mt-1">
                        Customize chapter headings, page breaks, and section styling
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {hasUnsavedChanges && (
                        <Badge variant="outline" className="text-amber-600 border-amber-600">
                            Unsaved changes
                        </Badge>
                    )}
                    <Button onClick={handleSave} data-testid="save-formatting-btn">
                        <Save className="h-4 w-4 mr-2" />
                        Save Settings
                    </Button>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-6">
                    <TabsTrigger value="headings" data-testid="tab-headings">
                        <Heading1 className="h-4 w-4 mr-2" />
                        Chapter Headings
                    </TabsTrigger>
                    <TabsTrigger value="breaks" data-testid="tab-breaks">
                        <SplitSquareVertical className="h-4 w-4 mr-2" />
                        Page Breaks
                    </TabsTrigger>
                    <TabsTrigger value="sections" data-testid="tab-sections">
                        <FileText className="h-4 w-4 mr-2" />
                        Section Breaks
                    </TabsTrigger>
                </TabsList>

                {/* Chapter Headings Tab */}
                <TabsContent value="headings">
                    <div className="grid grid-cols-3 gap-6">
                        {/* Presets */}
                        <div className="col-span-1">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Palette className="h-4 w-4" />
                                Preset Styles
                            </h3>
                            <ScrollArea className="h-[400px] pr-3">
                                <div className="space-y-2">
                                    {Object.entries(HEADING_PRESETS).map(([id, preset]) => (
                                        <button
                                            key={id}
                                            className={`w-full p-3 text-left rounded-lg border transition-colors ${
                                                selectedPreset === id 
                                                    ? 'border-accent bg-accent/5' 
                                                    : 'border-border hover:border-accent/50'
                                            }`}
                                            onClick={() => handlePresetChange(id)}
                                            data-testid={`preset-${id}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium">{preset.name}</span>
                                                {selectedPreset === id && (
                                                    <Check className="h-4 w-4 text-accent" />
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {preset.description}
                                            </p>
                                        </button>
                                    ))}
                                    {/* Custom option */}
                                    <button
                                        className={`w-full p-3 text-left rounded-lg border transition-colors ${
                                            selectedPreset === 'custom' 
                                                ? 'border-accent bg-accent/5' 
                                                : 'border-border hover:border-accent/50'
                                        }`}
                                        onClick={() => setSelectedPreset('custom')}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">Custom</span>
                                            {selectedPreset === 'custom' && (
                                                <Check className="h-4 w-4 text-accent" />
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Create your own unique chapter style
                                        </p>
                                    </button>
                                </div>
                            </ScrollArea>
                        </div>

                        {/* Customization Options */}
                        <div className="col-span-1 border-l pl-6">
                            <h3 className="font-semibold mb-3">Customize</h3>
                            <ScrollArea className="h-[400px] pr-3">
                                <div className="space-y-4">
                                    <div>
                                        <Label>Chapter Prefix</Label>
                                        <Input
                                            value={customSettings.chapterPrefix}
                                            onChange={(e) => updateCustomSetting('chapterPrefix', e.target.value)}
                                            placeholder="Chapter, Part, etc."
                                            className="mt-1"
                                        />
                                    </div>
                                    <div>
                                        <Label>Show Number</Label>
                                        <Select 
                                            value={customSettings.showNumber ? 'yes' : 'no'}
                                            onValueChange={(v) => updateCustomSetting('showNumber', v === 'yes')}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="yes">Yes</SelectItem>
                                                <SelectItem value="no">No</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Number Style</Label>
                                        <Select 
                                            value={customSettings.numberStyle}
                                            onValueChange={(v) => updateCustomSetting('numberStyle', v)}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="numeric">1, 2, 3...</SelectItem>
                                                <SelectItem value="roman">I, II, III...</SelectItem>
                                                <SelectItem value="spelled">One, Two, Three...</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Alignment</Label>
                                        <Select 
                                            value={customSettings.alignment}
                                            onValueChange={(v) => updateCustomSetting('alignment', v)}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="left">Left</SelectItem>
                                                <SelectItem value="center">Center</SelectItem>
                                                <SelectItem value="right">Right</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Title Case</Label>
                                        <Select 
                                            value={customSettings.titleCase}
                                            onValueChange={(v) => updateCustomSetting('titleCase', v)}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">As Written</SelectItem>
                                                <SelectItem value="uppercase">UPPERCASE</SelectItem>
                                                <SelectItem value="lowercase">lowercase</SelectItem>
                                                <SelectItem value="capitalize">Title Case</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Font Size</Label>
                                        <Select 
                                            value={customSettings.fontSize}
                                            onValueChange={(v) => updateCustomSetting('fontSize', v)}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="small">Small</SelectItem>
                                                <SelectItem value="medium">Medium</SelectItem>
                                                <SelectItem value="large">Large</SelectItem>
                                                <SelectItem value="xlarge">Extra Large</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Font Family</Label>
                                        <Select 
                                            value={customSettings.fontFamily}
                                            onValueChange={(v) => updateCustomSetting('fontFamily', v)}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="serif">Serif (Traditional)</SelectItem>
                                                <SelectItem value="sans">Sans-Serif (Modern)</SelectItem>
                                                <SelectItem value="script">Script (Elegant)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Decoration</Label>
                                        <Select 
                                            value={customSettings.decoration}
                                            onValueChange={(v) => updateCustomSetting('decoration', v)}
                                        >
                                            <SelectTrigger className="mt-1">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">None</SelectItem>
                                                <SelectItem value="underline">Underline</SelectItem>
                                                <SelectItem value="ornament">Ornament (❧)</SelectItem>
                                                <SelectItem value="flourish">Flourish (~ ✦ ~)</SelectItem>
                                                <SelectItem value="bold-line">Bold Line</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </ScrollArea>
                        </div>

                        {/* Preview */}
                        <div className="col-span-1 border-l pl-6">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Eye className="h-4 w-4" />
                                Preview
                            </h3>
                            <ScrollArea className="h-[400px]">
                                <div className="space-y-4">
                                    {(chapters?.length > 0 ? chapters.slice(0, 3) : [{ title: 'The Adventure Begins' }, { title: 'Into the Unknown' }, { title: 'The Final Chapter' }]).map((ch, idx) => (
                                        <div key={idx}>
                                            {renderChapterPreview(ch, idx)}
                                        </div>
                                    ))}
                                </div>
                            </ScrollArea>
                        </div>
                    </div>
                </TabsContent>

                {/* Page Breaks Tab */}
                <TabsContent value="breaks">
                    <div className="max-w-2xl">
                        <h3 className="font-semibold mb-4">Page Break Settings</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Control where new pages begin in your exported book.
                        </p>
                        <div className="space-y-3">
                            {Object.entries(PAGE_BREAK_OPTIONS).map(([id, option]) => (
                                <button
                                    key={id}
                                    className={`w-full p-4 text-left rounded-lg border transition-colors ${
                                        pageBreaks === id 
                                            ? 'border-accent bg-accent/5' 
                                            : 'border-border hover:border-accent/50'
                                    }`}
                                    onClick={() => { setPageBreaks(id); setHasUnsavedChanges(true); }}
                                    data-testid={`pagebreak-${id}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium">{option.label}</span>
                                        {pageBreaks === id && (
                                            <Check className="h-4 w-4 text-accent" />
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {option.description}
                                    </p>
                                </button>
                            ))}
                        </div>
                    </div>
                </TabsContent>

                {/* Section Breaks Tab */}
                <TabsContent value="sections">
                    <div className="max-w-2xl">
                        <h3 className="font-semibold mb-4">Section Break Style</h3>
                        <p className="text-sm text-muted-foreground mb-6">
                            Choose how to visually separate scenes or sections within chapters.
                        </p>
                        <div className="grid grid-cols-3 gap-3">
                            {SECTION_BREAK_STYLES.map((style) => (
                                <button
                                    key={style.id}
                                    className={`p-4 text-center rounded-lg border transition-colors ${
                                        sectionBreakStyle === style.id 
                                            ? 'border-accent bg-accent/5' 
                                            : 'border-border hover:border-accent/50'
                                    }`}
                                    onClick={() => { setSectionBreakStyle(style.id); setHasUnsavedChanges(true); }}
                                    data-testid={`section-${style.id}`}
                                >
                                    <div className="text-2xl mb-2 h-8 flex items-center justify-center">
                                        {style.id === 'custom' ? customSectionBreak : style.preview}
                                    </div>
                                    <div className="text-sm font-medium">{style.label}</div>
                                </button>
                            ))}
                        </div>
                        {sectionBreakStyle === 'custom' && (
                            <div className="mt-4">
                                <Label>Custom Section Break Character(s)</Label>
                                <Input
                                    value={customSectionBreak}
                                    onChange={(e) => { setCustomSectionBreak(e.target.value); setHasUnsavedChanges(true); }}
                                    placeholder="Enter your custom separator"
                                    className="mt-1 max-w-xs"
                                    maxLength={10}
                                />
                            </div>
                        )}

                        {/* Preview */}
                        <div className="mt-8 p-6 bg-white dark:bg-zinc-900 rounded-lg border">
                            <h4 className="text-sm font-medium text-muted-foreground mb-4">Preview</h4>
                            <div className="prose prose-sm max-w-none">
                                <p>
                                    Sarah walked through the garden, her mind racing with possibilities. 
                                    The sun was setting, casting long shadows across the path.
                                </p>
                                <div className="text-center text-lg my-6 text-muted-foreground">
                                    {sectionBreakStyle === 'custom' ? customSectionBreak : SECTION_BREAK_STYLES.find(s => s.id === sectionBreakStyle)?.preview}
                                </div>
                                <p>
                                    The next morning brought new challenges. James was already at the 
                                    office when she arrived, his expression unreadable.
                                </p>
                            </div>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default ProBookFormatting;
